import { FONT_FAMILY_CONFIG } from 'constants/fonts';
import { useEffect, useState } from 'react';
import { CustomFontState } from 'types/customFont.types';
import { isTest } from 'utils/environment';
import WebFont from 'webfontloader';

import { createGoogleFontsUrl } from './createGoogleFontsUrl';
import { isCustomFont, loadCustomFonts } from './customFontUtils';
import { isMsFont, loadMsFonts } from './msFontLoader';
import { parseFont } from './useFont.helpers';

const resolvedFonts = new Set();

export const filterUnloadedFamilies = (families: string[]) => families.filter((family) => !resolvedFonts.has(family));

export const isValidWebFont = (family: string) => FONT_FAMILY_CONFIG[family as keyof typeof FONT_FAMILY_CONFIG];

export const loadFonts = (families: string[], customFontsMap: Map<string, CustomFontState> = new Map()) =>
  new Promise((resolve, reject) => {
    const unloadedFamilies: string[] = [];
    const unloadedCustomFamilies: string[] = [];
    const unloadedMsFamilies: string[] = [];
    const unloadedGoogleFamilies: string[] = [];

    families.forEach((family) => {
      // some fonts can be wrapped with ' or " so we need to remove these characters else the font wont load
      family = parseFont(family);
      const isLoaded = resolvedFonts.has(family);

      if (!isLoaded && !!family) {
        unloadedFamilies.push(family);

        // Check if it is a MS font
        if (isMsFont(family)) {
          unloadedMsFamilies.push(family);
        }

        // Check if it is a custom uploaded font
        else if (isCustomFont(family)) {
          unloadedCustomFamilies.push(family);
        }

        // Assume all remainning fonts to be google fonts.
        else if (isValidWebFont(family)) {
          unloadedGoogleFamilies.push(family);
        }
      }
    });

    if (unloadedFamilies.length === 0) {
      resolve(true);
      return;
    }

    // skip loading fonts in test environment
    if (isTest) return resolve(true);

    // Note: if FontFace API not work well, we can fallback to @font-face css loading method
    loadMsFonts(unloadedMsFamilies).then((msFamilies) => {
      msFamilies.forEach(resolvedFonts.add, resolvedFonts);
    });

    loadCustomFonts(unloadedCustomFamilies, customFontsMap).then((customFamilies) => {
      customFamilies.forEach(resolvedFonts.add, resolvedFonts);
    });

    if (unloadedGoogleFamilies.length === 0) {
      resolve(true);
      return;
    }

    // Google fonts
    WebFont.load({
      custom: {
        families,
        urls: [createGoogleFontsUrl(unloadedGoogleFamilies)],
      },
      active() {
        // Add resolved font into cache
        unloadedGoogleFamilies.forEach(resolvedFonts.add, resolvedFonts);
        resolve(true);
      },
      inactive() {
        const message = `Fail to load fonts: ${JSON.stringify(unloadedFamilies)}`;
        reject(new Error(message));
      },
    });
  });

export const useFontLoader = (families: string[]) => {
  const [state, setState] = useState(false);

  useEffect(() => {
    const unloadedFamilies = filterUnloadedFamilies(families);
    if (unloadedFamilies.length === 0) return;

    loadFonts(unloadedFamilies)
      .then(() => {
        setState(true);
      })
      .catch((e) => {
        console.debug(e);
        // TODO: need to retry if fonts fail to fetch?
      });
  }, [families]);

  return state;
};

// Re-export useful utilities
export { isCustomFont, isMsFont };
